import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Card, Grid, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import { endOfDay, formatISO, startOfDay } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

import { CommonDateRangeSelectionButtons, Page } from "@APP/components";
import { NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION, commonFormatDate } from "@APP/constants";
import { AppState, hideLoader, setUserSearchResults, showLoader } from "@APP/redux";
import { formatErrorMessage } from "@APP/utils";
import { anonymizeUser, getSearchedUsers } from "@APP/services/api";
import { UserSearchResponse } from "@APP/types";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";

import UserList from "./UserList";

export const UserDashboardView = () => {
  const alert = useAlert();
  const dispatch = useDispatch();

  const [entries, setEntries] = useState(25);
  const [userData, setUserData] = useState<UserSearchResponse[]>([]);
  const [lastPage, setLastPage] = useState(0);
  const [submitFrom, setSubmitFrom] = useState("");
  const [submitTo, setSubmitTo] = useState("");
  const [page, setPage] = useState(0);
  const [customDatePicker, setCustomDatePicker] = useState(false);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState("createdDate");

  const { from, to } = useSelector(
    ({
      userAdmin: {
        rtpDashboard: { from, to },
      },
    }: AppState) => ({
      from,
      to,
    }),
  );

  const handleErrorCodes = useHandleErrorCodes();

  const fetchUsers = async ({ from, to }: { from: string; to: string }) => {
    const utcFrom = zonedTimeToUtc(startOfDay(new Date(from)), "UTC").toISOString();
    const utcTo = zonedTimeToUtc(endOfDay(new Date(to)), "UTC").toISOString();

    try {
      let sort_by = "";
      switch (orderBy) {
        case "username":
          sort_by = "username";
          break;
        case "organizationName":
          sort_by = "org_name";
          break;
        case "createdDate":
        case "createdTime":
          sort_by = "created_at";
          break;
        case "status":
          sort_by = "status";
          break;
        default:
          break;
      }
      dispatch(showLoader());
      const { data: userData, meta } = await getSearchedUsers(
        { from: utcFrom, to: utcTo },
        { page: page, entries: entries },
        `sort_by=${sort_by}&sort_type=${order}`,
      );
      if (meta.totalItems) {
        if (meta.totalItems <= entries) {
          setLastPage(1);
        } else {
          const lastPageIndex = Math.ceil(meta.totalItems / entries);
          setLastPage(lastPageIndex);
        }
      }
      dispatch(setUserSearchResults(userData));
      setUserData(userData);
    } catch (error: any) {
      const errorData = error?.resposnse?.data;
      const isHandled = handleErrorCodes(errorData?.errorCode);

      if (!isHandled) return;
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    const dateString = new Date().toISOString();
    fetchUsers({ from: submitFrom || dateString, to: submitTo || dateString });
  }, [entries, page, order, orderBy]);

  const handleOnSubmit = ({ from, to }: { from: string; to: string }) => {
    fetchUsers({ from, to });
    setSubmitFrom(from);
    setSubmitTo(to);
    setPage(0);
  };

  const { errors, handleSubmit, touched, values, setFieldValue } = useFormik({
    initialValues: {
      from: zonedTimeToUtc(from, "UTC").toISOString(),
      to: zonedTimeToUtc(to, "UTC").toISOString(),
    },
    onSubmit: handleOnSubmit,
  });

  const handleOnEntriesChange = (entries: number) => {
    setPage(NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION);
    setEntries(entries);
  };

  useEffect(() => {
    const from = formatISO(startOfDay(new Date()));
    const to = formatISO(new Date());
    const utcFrom = zonedTimeToUtc(from, "UTC").toISOString();
    const utcTo = zonedTimeToUtc(to, "UTC").toISOString();
    fetchUsers({ from: utcFrom, to: utcTo });
  }, []);

  const handleAnonymize = async (orgId: string, customerId: string) => {
    try {
      dispatch(showLoader());
      await anonymizeUser(orgId, customerId);

      // Refresh the user list to get updated anonymisation status
      const dateString = new Date().toISOString();
      await fetchUsers({ from: submitFrom || dateString, to: submitTo || dateString });

      alert.open("Success", "Anonymisation process has been started successfully.", [
        {
          text: "Okay",
        },
      ]);
    } catch (error: any) {
      const errorData = error?.response?.data;

      // If no error code exists, show generic message
      if (!errorData?.errorCode) {
        alert.open(
          "Error",
          "Unable to start anonymisation process. Please try again or contact support if the issue persists.",
          [{ text: "Okay" }],
        );
      } else {
        // If error code exists, try to handle it
        const isHandled = handleErrorCodes(errorData.errorCode);

        if (!isHandled) {
          const errorMessage = formatErrorMessage(
            error,
            "Unable to start anonymisation process. Please try again or contact support if the issue persists.",
          );
          alert.open("Error", errorMessage, [{ text: "Okay" }]);
        }
      }
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <>
      <Page title="User Dashboard">
        <form onSubmit={handleSubmit}>
          <Card elevation={4}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box p={2}>
                  <Grid container item spacing={1}>
                    <Grid item xs={12} md={8} lg={8}>
                      <CommonDateRangeSelectionButtons
                        fieldValueSetter={setFieldValue}
                        contentJustify="space-evenly"
                        values={values}
                        setCustomDatePicker={setCustomDatePicker}
                      />
                    </Grid>
                    {customDatePicker && (
                      <Grid container item direction="column" xs={12} sm={4} md={4} spacing={1}>
                        <Grid style={{ display: "flex", gap: "8px" }} item>
                          <DatePicker
                            renderInput={(props) => (
                              <TextField
                                error={Boolean(touched.from && errors.from)}
                                helperText={touched.from && errors.from}
                                fullWidth
                                size="small"
                                onClick={() => setStartDatePickerOpen(true)}
                                {...props}
                              />
                            )}
                            onChange={(value) => {
                              setFieldValue("from", value);
                            }}
                            value={values.from}
                            label="Start Date"
                            inputFormat={commonFormatDate}
                            disableFuture
                            open={startDatePickerOpen}
                            onOpen={() => setStartDatePickerOpen(true)}
                            onClose={() => setStartDatePickerOpen(false)}
                          />

                          <DatePicker
                            renderInput={(props) => (
                              <TextField
                                error={Boolean(touched.to && errors.to)}
                                helperText={touched.to && errors.to}
                                fullWidth
                                size="small"
                                onClick={() => setEndDatePickerOpen(true)}
                                label="End Date"
                                {...props}
                              />
                            )}
                            onChange={(value) => {
                              setFieldValue("to", value);
                            }}
                            value={values.to}
                            label="End Date"
                            inputFormat={commonFormatDate}
                            disableFuture
                            open={endDatePickerOpen}
                            onOpen={() => setEndDatePickerOpen(true)}
                            onClose={() => setEndDatePickerOpen(false)}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Box>
                {customDatePicker && (
                  <>
                    <Box p={2} style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Grid container item style={{ display: "contents" }}>
                        <Grid item lg={4} xs={false} />
                        <Grid item lg={4} xs={12}>
                          <Button color="primary" fullWidth type="submit" variant="contained">
                            View
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          </Card>
          <UserList
            data={userData}
            entries={entries}
            lastPage={lastPage}
            page={page}
            setPage={setPage}
            handleOnEntriesChange={handleOnEntriesChange}
            order={order}
            setOrder={setOrder}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            onAnonymize={handleAnonymize}
          />
        </form>
      </Page>
    </>
  );
};

export default UserDashboardView;
